import { URL_PREFIX } from '../constants/api';
import { APIError } from './errors';

// API Response Types (matching refactored backend schema)
export interface StreamsByVintageSeriesResponse {
  date: string;
  vintage: string;
  revenue: number;
  revenue_prev: number;
}

export interface StreamsByVintageTotalsResponse {
  vintage: string;
  streams_tw: number;
  streams_lw: number;
  streams_fytd: number;
  streams_fytd_prev: number;
  order_index: number;
}

export interface StreamsByVintageWeeklySeriesResponse {
  date: string;
  vintage: string;
  streams: number;
}

// Transformed Types for Frontend
export interface VintageRevenueChartItem {
  date: string;
  newRelease: number;
  shallow: number;
  deepCatalog: number;
  [key: string]: string | number;
}

export interface VintageGrowthChartItem {
  date: string;
  newRelease: number;
  shallow: number;
  deepCatalog: number;
  grandTotal: number;
  [key: string]: string | number;
}

export interface VintageStreamsChartItem {
  date: string;
  newRelease: number;
  shallow: number;
  deepCatalog: number;
  [key: string]: string | number;
}

export interface VintageTableItem {
  vintage: string;
  thisWeek: number;
  lastWeek: number;
  fytd: number;
  fytdPrev: number;
  fytdChange: number;
  fytdPercent: number;
  fytdPrevPercent: number;
  shareDiff: number;
  orderIndex: number;
}

// Vintage name mapping from API response to our keys
const VINTAGE_MAP: Record<string, string> = {
  'New Release': 'newRelease',
  'Shallow Catalog': 'shallow',
  'Deep Catalog': 'deepCatalog',
};

/**
 * Format date to display format (e.g., "Sep-24")
 */
const formatDateToMonthYear = (dateStr: string): string => {
  const date = new Date(dateStr);

  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: '2-digit',
  });
};

/**
 * Fetch revenue by vintage series data (for stacked bar chart)
 */
export const getStreamsRevenueByVintage = async (): Promise<
  VintageRevenueChartItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/revenue-by-vintage-series`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch revenue by vintage series: ${response.statusText}`,
        response.status,
      );
    }

    const data = (await response.json()) as StreamsByVintageSeriesResponse[];

    // Transform API response to chart format
    // API returns flat list: [{ date, vintage, revenue, revenue_prev }, ...]
    // We need to pivot to date-based objects with vintage values

    const dateMap = new Map<
      string,
      {
        rawDate: Date;
        newRelease: number;
        shallow: number;
        deepCatalog: number;
      }
    >();

    data.forEach((item) => {
      const vintageKey = VINTAGE_MAP[item.vintage];

      if (!vintageKey) {
        return; // Skip unknown vintages
      }

      const rawDate = new Date(item.date);
      const dateStr = formatDateToMonthYear(item.date);

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          rawDate,
          newRelease: 0,
          shallow: 0,
          deepCatalog: 0,
        });
      }

      const dateEntry = dateMap.get(dateStr)!;

      // Convert to millions for display
      dateEntry[vintageKey as keyof Omit<typeof dateEntry, 'rawDate'>] =
        item.revenue / 1_000_000;
    });

    // Sort by date and convert to array
    const sortedEntries = Array.from(dateMap.entries()).sort(
      (a, b) => a[1].rawDate.getTime() - b[1].rawDate.getTime(),
    );

    const chartData: VintageRevenueChartItem[] = sortedEntries.map(
      ([date, values]) => ({
        date,
        newRelease: values.newRelease,
        shallow: values.shallow,
        deepCatalog: values.deepCatalog,
      }),
    );

    return chartData;
  } catch (error) {
    console.warn('Failed to fetch revenue by vintage series:', error);

    throw error;
  }
};

/**
 * Fetch revenue growth by vintage data (for YoY growth line chart)
 * Uses the same endpoint as revenue series but calculates growth from revenue vs revenue_prev
 */
export const getStreamsRevenueGrowthByVintage = async (): Promise<
  VintageGrowthChartItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/revenue-by-vintage-series`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch revenue by vintage series: ${response.statusText}`,
        response.status,
      );
    }

    const data = (await response.json()) as StreamsByVintageSeriesResponse[];

    // Transform API response to chart format
    // API returns flat list: [{ date, vintage, revenue, revenue_prev }, ...]
    // We need to pivot to date-based objects with growth percentages

    const dateMap = new Map<
      string,
      {
        rawDate: Date;
        newRelease: number;
        shallow: number;
        deepCatalog: number;
        totalCurr: number;
        totalPrev: number;
      }
    >();

    data.forEach((item) => {
      const vintageKey = VINTAGE_MAP[item.vintage];

      if (!vintageKey) {
        return; // Skip unknown vintages
      }

      const rawDate = new Date(item.date);
      const dateStr = formatDateToMonthYear(item.date);

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          rawDate,
          newRelease: 0,
          shallow: 0,
          deepCatalog: 0,
          totalCurr: 0,
          totalPrev: 0,
        });
      }

      const dateEntry = dateMap.get(dateStr)!;

      // Calculate growth percentage for this vintage
      const growthPercent =
        item.revenue_prev > 0
          ? ((item.revenue - item.revenue_prev) / item.revenue_prev) * 100
          : 0;

      dateEntry[vintageKey as 'newRelease' | 'shallow' | 'deepCatalog'] =
        Number(growthPercent.toFixed(1));

      // Accumulate for grand total calculation
      dateEntry.totalCurr += item.revenue;
      dateEntry.totalPrev += item.revenue_prev;
    });

    // Sort by date and convert to array
    const sortedEntries = Array.from(dateMap.entries()).sort(
      (a, b) => a[1].rawDate.getTime() - b[1].rawDate.getTime(),
    );

    const chartData: VintageGrowthChartItem[] = sortedEntries.map(
      ([date, values]) => ({
        date,
        newRelease: values.newRelease,
        shallow: values.shallow,
        deepCatalog: values.deepCatalog,
        grandTotal:
          values.totalPrev > 0
            ? Number(
                (
                  ((values.totalCurr - values.totalPrev) / values.totalPrev) *
                  100
                ).toFixed(1),
              )
            : 0,
      }),
    );

    return chartData;
  } catch (error) {
    console.warn('Failed to fetch revenue growth by vintage:', error);

    throw error;
  }
};

/**
 * Fetch streams by vintage totals data (for table)
 */
export const getStreamsByVintageTotals = async (): Promise<
  VintageTableItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/streams-by-vintage-totals`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch streams by vintage totals: ${response.statusText}`,
        response.status,
      );
    }

    const data = (await response.json()) as StreamsByVintageTotalsResponse[];

    // Calculate totals for percentages
    const totalFytd = data.reduce((sum, item) => sum + item.streams_fytd, 0);
    const totalFytdPrev = data.reduce(
      (sum, item) => sum + item.streams_fytd_prev,
      0,
    );

    return data.map((item) => {
      const fytdChange =
        item.streams_fytd_prev > 0
          ? ((item.streams_fytd - item.streams_fytd_prev) /
              item.streams_fytd_prev) *
            100
          : 0;

      const fytdPercent =
        totalFytd > 0
          ? Number(((item.streams_fytd / totalFytd) * 100).toFixed(1))
          : 0;

      const fytdPrevPercent =
        totalFytdPrev > 0
          ? Number(((item.streams_fytd_prev / totalFytdPrev) * 100).toFixed(1))
          : 0;

      return {
        vintage: item.vintage,
        thisWeek: item.streams_tw,
        lastWeek: item.streams_lw,
        fytd: item.streams_fytd,
        fytdPrev: item.streams_fytd_prev,
        fytdChange: Number(fytdChange.toFixed(1)),
        fytdPercent,
        fytdPrevPercent,
        shareDiff: Number((fytdPercent - fytdPrevPercent).toFixed(1)),
        orderIndex: item.order_index,
      };
    });
  } catch (error) {
    console.warn('Failed to fetch streams by vintage totals:', error);

    throw error;
  }
};

/**
 * Fetch streams by vintage weekly series data (for line chart)
 */
export const getStreamsByVintageSeries = async (): Promise<
  VintageStreamsChartItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/streams-by-vintage-series`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch streams by vintage series: ${response.statusText}`,
        response.status,
      );
    }

    const data =
      (await response.json()) as StreamsByVintageWeeklySeriesResponse[];

    // Transform API response to chart format
    // API returns flat list: [{ date, vintage, streams }, ...]
    // We need to pivot to date-based objects with vintage values

    const dateMap = new Map<
      string,
      {
        rawDate: Date;
        newRelease: number;
        shallow: number;
        deepCatalog: number;
      }
    >();

    data.forEach((item) => {
      const vintageKey = VINTAGE_MAP[item.vintage];

      if (!vintageKey) {
        return; // Skip unknown vintages
      }

      const rawDate = new Date(item.date);
      const dateStr = formatDateToMonthYear(item.date);

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          rawDate,
          newRelease: 0,
          shallow: 0,
          deepCatalog: 0,
        });
      }

      const dateEntry = dateMap.get(dateStr)!;

      // Store streams value (convert to billions for display)
      dateEntry[vintageKey as keyof Omit<typeof dateEntry, 'rawDate'>] =
        item.streams / 1_000_000_000;
    });

    // Sort by date and convert to array
    const sortedEntries = Array.from(dateMap.entries()).sort(
      (a, b) => a[1].rawDate.getTime() - b[1].rawDate.getTime(),
    );

    const chartData: VintageStreamsChartItem[] = sortedEntries.map(
      ([date, values]) => ({
        date,
        newRelease: values.newRelease,
        shallow: values.shallow,
        deepCatalog: values.deepCatalog,
      }),
    );

    return chartData;
  } catch (error) {
    console.warn('Failed to fetch streams by vintage series:', error);

    throw error;
  }
};
